import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { AxiosError } from 'axios';
import { parse } from 'qs';

import { useCapyValues } from '@/hooks/useCapyValues';
import i18n from '@/settings/i18n';
import { sentryReportError } from '@/utils/sentryReportError';
import { pages } from '@/settings/pages';
import { adminApi } from '@/services/api';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';

import { ActivationStart, Values } from '../Presentation';
import { stringify } from 'query-string';

export const ActivationStartContainer: React.FC = () => {
  const { flashError } = useFlashMessenger();
  const [isLoading, setIsLoading] = useState(false);
  const [capyError, setCapyError] = useState(false);
  const { setCapyValues, createParamsWithCapyValues } = useCapyValues();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = parse(location.search, { ignoreQueryPrefix: true }) as {
    email?: string;
    locale?: string;
  };
  const initialEmail = searchParams.email || '';

  useEffect(() => {
    const { locale } = searchParams;
    if (locale) {
      i18n.changeLanguage(locale);
      delete searchParams.locale;
      navigate(
        {
          pathname: location.pathname,
          search: stringify(searchParams),
        },
        { replace: true },
      );
    }
  }, [location, navigate, searchParams]);

  const onSubmit = useCallback(
    async (values: Values) => {
      try {
        setIsLoading(true);
        setCapyError(false);
        const data = createParamsWithCapyValues(values);
        await adminApi.activateAccountMail({ data });
        navigate(pages.token.sent);
      } catch (err) {
        if (err instanceof AxiosError && err.response !== undefined) {
          if (err.response && err.response.data.error === 'already_activated') {
            navigate(pages.activation.alreadyCompleted);
          } else if (err.response && err.response.status === 401) {
            setCapyError(true);
          } else {
            sentryReportError(err);
            flashError(i18n.t('ui.error.unexpected'));
          }
          setIsLoading(false);
        }
      }
    },
    [createParamsWithCapyValues, flashError, navigate],
  );

  const childProps = {
    initialEmail,
    isLoading,
    capyError,
    onSubmit,
    setCapyValues,
  };
  return <ActivationStart {...childProps} />;
};
